/**
 * Request Throttler with Rate Limit Handling
 * Implements:
 * - Request batching to reduce API calls
 * - Exponential backoff for rate limit errors
 * - Request queue with priority
 * - Cache layer to prevent redundant calls
 */

interface QueuedRequest<T> {
  key: string;
  priority: number;
  execute: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (error: any) => void;
  retries: number;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresIn: number;
}

class RequestThrottler {
  private queue: QueuedRequest<any>[] = [];
  private processing = false;
  private cache = new Map<string, CacheEntry<any>>();
  private requestsInFlight = 0;
  private maxConcurrent = 3; // Max concurrent requests (reduced for AI endpoints)
  private minDelay = 250; // Min delay between requests (ms) - increased for rate limiting
  private lastRequestTime = 0;
  private rateLimitResetTime: number | null = null;
  private consecutiveRateLimits = 0; // Track consecutive rate limits

  /**
   * Add request to queue with optional caching
   */
  async enqueue<T>(
    key: string,
    execute: () => Promise<T>,
    options: {
      priority?: number;
      cacheTime?: number; // Cache duration in ms
      maxRetries?: number;
    } = {}
  ): Promise<T> {
    const { priority = 1, cacheTime = 0, maxRetries = 3 } = options;

    // Check cache first
    if (cacheTime > 0) {
      const cached = this.getFromCache<T>(key, cacheTime);
      if (cached !== null) {
        return cached;
      }
    }

    // Create promise for this request
    return new Promise((resolve, reject) => {
      this.queue.push({
        key,
        priority,
        execute,
        resolve,
        reject,
        retries: 0,
      });

      // Start processing if not already
      if (!this.processing) {
        this.processQueue();
      }
    });
  }

  /**
   * Process queued requests with rate limiting
   */
  private async processQueue() {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    this.processing = true;

    while (this.queue.length > 0) {
      // Wait if we hit rate limit
      if (this.rateLimitResetTime && Date.now() < this.rateLimitResetTime) {
        const waitTime = this.rateLimitResetTime - Date.now();
        console.log(`Rate limit active, waiting ${Math.ceil(waitTime / 1000)}s`);
        await this.sleep(waitTime);
        this.rateLimitResetTime = null;
      }

      // Respect max concurrent requests
      if (this.requestsInFlight >= this.maxConcurrent) {
        await this.sleep(100);
        continue;
      }

      // Respect min delay between requests
      const timeSinceLastRequest = Date.now() - this.lastRequestTime;
      if (timeSinceLastRequest < this.minDelay) {
        await this.sleep(this.minDelay - timeSinceLastRequest);
      }

      // Sort by priority (higher first)
      this.queue.sort((a, b) => b.priority - a.priority);

      // Process next request
      const request = this.queue.shift();
      if (request) {
        this.executeRequest(request);
      }
    }

    this.processing = false;
  }

  /**
   * Execute a single request with retry logic
   */
  private async executeRequest(request: QueuedRequest<any>) {
    this.requestsInFlight++;
    this.lastRequestTime = Date.now();

    try {
      const result = await request.execute();
      
      // Cache the result
      this.cache.set(request.key, {
        data: result,
        timestamp: Date.now(),
        expiresIn: 60000, // Default 1 minute
      });

      // Reset consecutive rate limit counter on success
      this.consecutiveRateLimits = 0;
      
      // Gradually reduce min delay if we're not hitting rate limits
      if (this.minDelay > 250) {
        this.minDelay = Math.max(this.minDelay * 0.9, 250);
      }
      
      request.resolve(result);
    } catch (error: any) {
      // Handle rate limit errors
      if (this.isRateLimitError(error)) {
        this.consecutiveRateLimits++;
        const retryAfter = this.getRetryAfter(error);
        
        // Increase max retries for rate limit errors to 5
        const maxRetries = request.retries < 5 ? 5 : request.retries;
        
        if (request.retries < maxRetries) {
          console.log(`Rate limit hit (${this.consecutiveRateLimits} consecutive), retrying request after ${Math.ceil(retryAfter / 1000)}s`);
          
          // Set rate limit reset time
          this.rateLimitResetTime = Date.now() + retryAfter;
          
          // Increase min delay if we're hitting rate limits frequently
          if (this.consecutiveRateLimits > 3) {
            this.minDelay = Math.min(this.minDelay * 1.5, 2000);
            console.log(`Increasing delay between requests to ${this.minDelay}ms`);
          }
          
          // Re-queue the request
          request.retries++;
          this.queue.unshift(request);
        } else {
          request.reject(new Error(`Max retries (${maxRetries}) exceeded for rate-limited request`));
        }
      } else if (request.retries < 3) {
        // Retry other errors with exponential backoff
        const backoffMs = Math.min(1000 * Math.pow(2, request.retries), 10000);
        await this.sleep(backoffMs);
        request.retries++;
        this.queue.unshift(request);
      } else {
        request.reject(error);
      }
    } finally {
      this.requestsInFlight--;
      
      // Continue processing queue
      if (this.queue.length > 0) {
        setTimeout(() => this.processQueue(), 0);
      }
    }
  }

  /**
   * Check if error is a rate limit error
   */
  private isRateLimitError(error: any): boolean {
    return (
      error?.status === 429 ||
      error?.code === 'RATE_LIMIT_EXCEEDED' ||
      error?.message?.toLowerCase().includes('rate limit') ||
      error?.details?.code === 'RATE_LIMIT_EXCEEDED'
    );
  }

  /**
   * Extract retry-after duration from error
   */
  private getRetryAfter(error: any): number {
    // Check for reset time in error details
    if (error?.details?.reset) {
      const resetTime = new Date(error.details.reset).getTime();
      const now = Date.now();
      const waitTime = Math.max(resetTime - now, 2000);
      // Add a small buffer (500ms) to ensure the rate limit has fully reset
      return waitTime + 500;
    }

    // Check for retry-after header value in seconds
    if (error?.details?.retryAfter) {
      return error.details.retryAfter * 1000 + 500;
    }

    // Default: exponential backoff based on consecutive rate limits
    // Start at 3 seconds, increase with each consecutive rate limit
    const baseDelay = 3000;
    const multiplier = Math.min(this.consecutiveRateLimits, 5);
    return baseDelay * Math.pow(1.5, multiplier);
  }

  /**
   * Get cached value if still valid
   */
  private getFromCache<T>(key: string, maxAge: number): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const age = Date.now() - entry.timestamp;
    if (age > maxAge) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  /**
   * Clear cache for specific key or all
   */
  clearCache(key?: string) {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Sleep utility
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get queue status
   */
  getStatus() {
    return {
      queueLength: this.queue.length,
      processing: this.processing,
      inFlight: this.requestsInFlight,
      cacheSize: this.cache.size,
      rateLimited: this.rateLimitResetTime ? this.rateLimitResetTime > Date.now() : false,
    };
  }
}

// Export singleton instance
export const requestThrottler = new RequestThrottler();

/**
 * Batch helper to group multiple operations
 */
export class BatchProcessor<T, R> {
  private batch: T[] = [];
  private batchSize: number;
  private flushTimer: NodeJS.Timeout | null = null;
  private flushDelay: number;
  private processor: (batch: T[]) => Promise<R[]>;

  constructor(
    processor: (batch: T[]) => Promise<R[]>,
    options: {
      batchSize?: number;
      flushDelay?: number;
    } = {}
  ) {
    this.processor = processor;
    this.batchSize = options.batchSize || 10;
    this.flushDelay = options.flushDelay || 1000;
  }

  /**
   * Add item to batch
   */
  async add(item: T): Promise<R> {
    return new Promise((resolve, reject) => {
      this.batch.push(item);

      // Store resolve/reject for this item
      const index = this.batch.length - 1;
      (item as any).__resolvers = { resolve, reject, index };

      // Auto-flush if batch is full
      if (this.batch.length >= this.batchSize) {
        this.flush();
      } else {
        // Schedule flush
        if (this.flushTimer) {
          clearTimeout(this.flushTimer);
        }
        this.flushTimer = setTimeout(() => this.flush(), this.flushDelay);
      }
    });
  }

  /**
   * Flush current batch
   */
  async flush() {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    if (this.batch.length === 0) {
      return;
    }

    const currentBatch = [...this.batch];
    this.batch = [];

    try {
      const results = await this.processor(currentBatch);

      // Resolve individual promises
      currentBatch.forEach((item, index) => {
        const resolvers = (item as any).__resolvers;
        if (resolvers && results[index] !== undefined) {
          resolvers.resolve(results[index]);
        }
      });
    } catch (error) {
      // Reject all promises in batch
      currentBatch.forEach((item) => {
        const resolvers = (item as any).__resolvers;
        if (resolvers) {
          resolvers.reject(error);
        }
      });
    }
  }
}
